import { CHART_CATEGORY_COLORS } from '../KestrixBarChart/styles.ts'
import { colorTokens } from '../tokens/colors.ts'

/** The subject types with a colour ramp, in the order badges should appear. */
export const SUBJECT_TYPE_TONE_KEYS = ['wall', 'roof', 'window', 'door', 'chimney', 'ground'] as const

export type SubjectTypeToneKey = (typeof SUBJECT_TYPE_TONE_KEYS)[number]

export type SubjectTypeTone = {
  readonly fill: string
  readonly border: string
}

const SUBJECT_TYPE_TONE_KEY_SET: ReadonlySet<string> = new Set(SUBJECT_TYPE_TONE_KEYS)

/**
 * Neutral pill tone for subject types with no colour ramp, and for the
 * overflow `+N` chip. `colorTokens.global.neutral` is not a monotonic ramp —
 * `500` is lighter than `100` — so this uses `100`/`200` exactly rather than
 * the shade numbers a monotonic ramp would suggest.
 */
export const NEUTRAL_PILL_TONE: SubjectTypeTone = {
  fill: colorTokens.global.neutral[100],
  border: colorTokens.global.neutral[200]
}

/**
 * Trims and lowercases a raw subject type so both wire casings resolve to
 * the same tone key. The ARV materialised view sends free-text lowercase
 * (`'roof'`), while `RetrofitAction.subjectType` sends the `AnnotationCategory`
 * enum (`ROOF`, `WALL`, `WINDOW`, `CHIMNEY`, `DOOR`, `UNKNOWN`, `GROUND`,
 * `THERMAL_AUTO`). Returns `null` for null, undefined, or an empty-after-trim
 * string. `THERMAL_AUTO` lowercases to `'thermal_auto'`, a real normalised
 * value with no tone.
 */
export const normaliseSubjectType = (raw: string | null | undefined): string | null => {
  if (raw === null || raw === undefined) {
    return null
  }
  const trimmed = raw.trim().toLowerCase()
  return trimmed === '' ? null : trimmed
}

/** Narrows a normalised subject type to a key with a colour ramp. */
export const isSubjectTypeToneKey = (value: string | null): value is SubjectTypeToneKey =>
  value !== null && SUBJECT_TYPE_TONE_KEY_SET.has(value)

/**
 * Resolves a raw subject type to its pill fill and border colours, reusing
 * the Insights page's ramp (`CHART_CATEGORY_COLORS.components`, index 3 for
 * fill and index 4 for border). Returns {@link NEUTRAL_PILL_TONE} for `null`,
 * `'unknown'`, `'thermal_auto'`, and any other unrecognised value — never a
 * guessed colour.
 */
export const subjectTypeTone = (value: string | null | undefined): SubjectTypeTone => {
  const normalised = normaliseSubjectType(value)
  if (!isSubjectTypeToneKey(normalised)) {
    return NEUTRAL_PILL_TONE
  }
  const ramp = CHART_CATEGORY_COLORS.components[normalised]
  return { fill: ramp[3], border: ramp[4] }
}

/**
 * Formats a raw subject type as a display label, title-casing each
 * underscore- or hyphen-separated word: `'wall'` becomes `'Wall'` and
 * `'thermal_auto'` becomes `'Thermal Auto'`. Returns `'Unknown'` for
 * null, undefined, or empty.
 */
export const formatSubjectTypeLabel = (value: string | null | undefined): string => {
  const normalised = normaliseSubjectType(value)
  if (normalised === null) {
    return 'Unknown'
  }
  return normalised
    .split(/[_-]/)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ')
}

/**
 * Sorts normalised subject types into {@link SUBJECT_TYPE_TONE_KEYS} order,
 * keeping any value outside that list afterwards in its original relative
 * order. Keeps badge colour order stable from row to row.
 */
export const orderSubjectTypesByTone = (values: readonly string[]): readonly string[] => {
  const known: string[] = []
  const unknown: string[] = []
  for (const value of values) {
    if (SUBJECT_TYPE_TONE_KEY_SET.has(value)) {
      known.push(value)
    } else {
      unknown.push(value)
    }
  }
  known.sort((a, b) => SUBJECT_TYPE_TONE_KEYS.indexOf(a as SubjectTypeToneKey) - SUBJECT_TYPE_TONE_KEYS.indexOf(b as SubjectTypeToneKey))
  return [...known, ...unknown]
}
